import * as CHDeviceSelectors from "@app/project/store/selectors/devices.selectors";

import { EventEmitter, Injectable } from "@angular/core";
import { CHDevice, DeviceClassObjectProperty, OutletInfoObject, SecurityFobInfo, SecurityZoneInfo } from "@app/project/model/ch-device";
import { DeviceInstallMetadata, DeviceMetadataParser, IDeviceMetadataNode } from "@app/project/services/device-metadata-parser";
import {
    CHServiceSelectors,
    DeviceMetadataSelectors,
    DeviceSelectors,
    DevicesActions,
    ProjectSelectors,
    ServiceMetadataSelectors,
} from "@app/project/store/";
import { RouterActions, RouterSelectors } from "@app/root-store/";
import { offlineClareStatus, onlineClareStatus, unconfiguredClareStatus } from "@app/shared/model/clare-status.model";
import { CLAREONE_SERVICE_ID, DEVICE_CLASSES } from "@app/shared/services/equator-api/ClareHomeRpc.constants";
import { Action, Store, select } from "@ngrx/store";
import {
    Observable,
    catchError,
    combineLatest,
    distinctUntilChanged,
    distinctUntilKeyChanged,
    filter,
    map,
    of,
    switchMap,
    take,
    tap,
} from "rxjs";
import {
    ClearLiteServiceState,
    ClearServiceState,
    DeleteServiceConfirmation,
    DeleteServiceMap,
    GetLiteServices,
    GetServiceToDeviceMap,
    UpdateServiceNameAndZone,
} from "../actions/ch-services.actions";
import { ClearDeviceMetadata, GetDeviceMetadata } from "../actions/device-metadata.actions";
import {
    ClearAllDCOs,
    ClearDCOState,
    ClearDevicesState,
    ClearZWaveDevicesState,
    DeleteAllDevices,
    DeleteDevice,
    DeleteSensorConfirmation,
    GetDeviceClassObjects,
    GetDevicesSilent,
    GetZWaveDevices,
    InvokeDeviceClassObjectOperation,
    ResetDCOState,
    ResetDevicesCallState,
    UpdateDeviceClassObject,
    UpdateDeviceName,
    UpdateDeviceNameSuccess,
} from "../actions/devices.actions";
import { ClearServiceMetadata, GetServiceMetadata } from "../actions/service-metadata.actions";
import { CHDeviceMetadata, CHDeviceState, CHServiceMetadataState } from "../clarehome.state";

import { ZoneSelectors } from "@app/area/store";
import { AuditLogInput } from "@app/audit-log/models/audit-log-type.model";
import { DeviceListItem } from "@app/device/components/device-list/device-list.component";
import { CHService } from "@app/project/model/ch-service";
import { accountRoutePaths } from "@app/project/project-routing-constants";
import { ProjectStatusService } from "@app/project/services/project-status.service";
import { SnackbarActions } from "@app/root-store/actions";
import { RouterService } from "@app/root-store/router.service";
import { ActionWord } from "@app/shared/resources/action-word";
import { isEmbedded } from "@app/shared/resources/embedded-mobile-checker";
import { ClareHomeDbCollection } from "@app/shared/resources/resource-objectifier";
import { AppHelperService } from "@app/shared/services/app-helper.service";
import { HttpResponseHandler } from "@app/shared/services/http-response-handler";
import { LocalizerService } from "@app/shared/services/localization/localizer.service";
import { ZoneDispatcher } from "../../../area/store/dispatchers/zones.dispatchers";
import { LoadingState } from "../../../root-store/api-result.state";
import { ProjectActions } from "../actions";
import { ProjectState } from "../projects.state";

@Injectable({
    providedIn: "root",
})
export class DeviceDispatchers {
    selectRoute() {
        return this.store.pipe(select(RouterSelectors.getUrl), take(1));
    }

    navigate(url, query?) {
        this.store.dispatch(RouterActions.routerGo({ path: url, query }));
        if (document.getElementById("mainContent")) {
            document.getElementById("mainContent").scrollTop = 0;
        }
    }

    navigateWithRedirect(url, redirectTo) {
        this.store.dispatch(RouterActions.routerGo({ path: url, query: { redirectTo } }));
    }

    getEditDeviceLink(uuid, projectId, serviceId) {
        let link = `myaccount/locations/${uuid}/devices/edit/${serviceId}`;

        if (projectId) {
            link = `accounts/edit/${projectId}/devices/edit/${serviceId}`;
        }

        return link;
    }

    dispatchAction(action: Action) {
        this.store.dispatch(action);
    }

    dispatchError(entityType: string, actionWord: ActionWord, errMsg?: string) {
        this.store.dispatch(
            SnackbarActions.openSnackbar({
                message: new HttpResponseHandler(entityType, actionWord, errMsg, true),
            })
        );
    }

    dispatchMessage(entityType: string, actionWord: ActionWord, errMsg?: string) {
        this.store.dispatch(
            SnackbarActions.openSnackbar({
                message: new HttpResponseHandler(entityType, actionWord, errMsg),
            })
        );
    }

    dispatchUpdateDeviceClassObject(payload) {
        return this.store.dispatch(new UpdateDeviceClassObject(payload));
    }

    dispatchInvokeDeviceClassObjectOperation(payload) {
        return this.store.dispatch(new InvokeDeviceClassObjectOperation(payload));
    }

    // Only used to update the state. The call to ClareHome should already have been made
    dispatchZoneDeviceNameUpdate(uuid: string, deviceUid: string, deviceName: string) {
        return this.store.dispatch(new UpdateDeviceNameSuccess({ uuid, deviceUid, deviceName }));
    }

    /**
     *
     * @param {string} uuid of controller
     * @param {string} deviceUid of device to update
     * @param {string} deviceName new name of device
     */
    dispatchDeviceNameUpdate(uuid: string, deviceUid: string, deviceName: string) {
        this.store.dispatch(new UpdateDeviceName({ uuid, deviceUid, deviceName }));
    }

    getDeviceClassObject(payload) {
        this.store.dispatch(new GetDeviceClassObjects(payload));
    }

    updateDeviceClassObject(
        uuid: string,
        deviceUid: string,
        deviceClass: string,
        property: string,
        args: string,
        callbackEmitter?: EventEmitter<Action>,
        logAction?: any
    ) {
        this.store.dispatch(new UpdateDeviceClassObject({ uuid, deviceUid, deviceClass, property, args, callbackEmitter, logAction }));
    }

    goToDeviceList() {
        combineLatest([this.findIdParam(), this.getUuid()])
            .pipe(
                tap(([id, uuid]) => {
                    let path;
                    if (id) {
                        path = `accounts/edit/${id}/devices/`;
                    } else if (uuid) {
                        path = `myaccount/locations/${uuid}/devices/edit`;
                    }
                    this.store.dispatch(
                        RouterActions.routerGo({
                            path: [path],
                        })
                    );
                }),
                take(1)
            )
            .subscribe();
    }

    navigateToCategory(category: string[]) {
        return this.routerService.navigateToCurrentUrlAndKeepQueryParams(category);
    }

    selectActiveProject() {
        return this.store.pipe(select(ProjectSelectors.getActiveProject), take(1));
    }

    selectActiveProjectStatus$() {
        return this.projectStatusService.getActiveProjectStatus$();
    }

    findIdParam() {
        return this.store.pipe(select(RouterSelectors.findIdParam));
    }

    findUrlParam(param: string) {
        return this.store.pipe(select(RouterSelectors.selectUrlParam(param)));
    }

    /**
     *
     * @param {CHDevice} device parent device of outlets to get
     * @param service
     * @returns {OutletInfoObject[]} list of wattbox outlets
     */
    getWattboxChildDeviceInfo$(device: CHDevice) {
        return this.store.pipe(select(CHDeviceSelectors.getWattboxChildDeviceInfo(device)));
    }

    getChildDeviceListForEnumeratedCP$(device: CHDevice, service: CHService) {
        return this.store.pipe(select(CHDeviceSelectors.getChildDeviceListForEnumeratedCP(device, service)));
    }

    getUuidForProject() {
        return this.store.pipe(select(ProjectSelectors.getActiveProjectUuid));
    }

    getUuid() {
        return this.store.pipe(select(ProjectSelectors.getUuid));
    }

    getPendingDeviceDeletions() {
        return this.store.pipe(select(DeviceSelectors.selectPendingDeletions));
    }

    getPendingServiceDeletions() {
        return this.store.pipe(select(CHServiceSelectors.selectPendingServiceDeletions));
    }

    selectServiceStatusCounts() {
        return this.store.pipe(select(CHServiceSelectors.getServiceStatusCounts));
    }

    isProjectOnline$<T>(result: T): Observable<T> {
        return this.store.pipe(
            select(ProjectSelectors.getActiveProjectStatus),
            filter(status => status === onlineClareStatus),
            map(_ => result)
        );
    }
    selectLiteServices() {
        return this.store.pipe(
            select(CHServiceSelectors.selectChLiteServices),
            filter(result => result && !!result.uuid),
            distinctUntilChanged(),
            switchMap(result => this.isProjectOnline$(result)),
            tap(result => {
                if (result.callState === LoadingState.init) {
                    return this.store.dispatch(new GetLiteServices({ uuid: result.uuid }));
                }
            }),
            map(result => result.result)
        );
    }

    selectDeviceZones(uid: string) {
        return this.store.pipe(select(ZoneSelectors.selectDeviceZones(uid)));
    }

    getStatus(service: CHService, liteService: CHService) {
        let status;
        if (service && service.isValid && service.isConfigured) {
            status = onlineClareStatus;
        } else if (!service.isConfigured) {
            status = unconfiguredClareStatus;
        } else {
            status = offlineClareStatus;
        }

        if (liteService) {
            if (!liteService.isConfigured) {
                status = unconfiguredClareStatus;
            } else if (!liteService.isValid) {
                status = offlineClareStatus;
            }
        }

        return status;
    }

    selectDeviceStatus(uid: string) {
        return this.store.pipe(select(DeviceSelectors.selectDeviceStatus(uid)));
    }

    getServiceCallState() {
        return this.store.pipe(
            select(CHServiceSelectors.selectChServicesState),
            distinctUntilChanged(),
            filter(result => !!result),
            map(result => result.callState)
        );
    }

    supportsMasterInstallerPinOperations(device: CHDevice) {
        let result = false;
        const deviceClassObjects = device.DeviceClassObjects;
        if (deviceClassObjects && deviceClassObjects[DEVICE_CLASSES.INTRUSION_USER_ADMIN]) {
            const operations = deviceClassObjects[DEVICE_CLASSES.INTRUSION_USER_ADMIN].Operations;
            if (
                operations !== null &&
                operations.includes("showInstallerPin") &&
                operations.includes("updateInstallerPin") &&
                operations.includes("updateMasterPin")
            ) {
                result = true;
            }
        }
        return result;
    }

    isDeviceUsedInService(service: CHService, deviceUid: string) {
        if (service.derivedServiceDevice === deviceUid) {
            return true;
        }

        for (const key in service.controlPoints) {
            if (service.controlPoints[key].deviceUid === deviceUid) {
                return true;
            }
        }
        return false;
    }

    selectServiceFromDevice(deviceUid: string) {
        return this.store.pipe(
            select(CHServiceSelectors.selectChServicesState),
            filter(services => !!services.result),
            map(services => {
                const foundServices = Object.values(services.result).filter(service => this.isDeviceUsedInService(service, deviceUid));
                if (foundServices) {
                    return foundServices[0];
                }
                return null;
            })
        );
    }

    selectDeviceAuthState(deviceUID) {
        return this.store.pipe(select(CHDeviceSelectors.selectDeviceClassObjectsForDevice(deviceUID, DEVICE_CLASSES.AUTHORIZATION)));
    }

    getDeviceCategoryName(category: string) {
        return LocalizerService.getDeviceCategory(category);
    }

    /**
     * @param {boolean} shouldExcludeZWave exclude z-wave sirens, by default is set to false
     * @returns {CHService[]} a list of security peripheral services
     */
    selectSecurityPeripheralDevices(shouldExcludeZWave = false) {
        return this.selectServices().pipe(
            filter(services => !!services),
            map(services =>
                Object.values(services)
                    .filter(service =>
                        shouldExcludeZWave ? AppHelperService.isExternalSonosSiren(service) : AppHelperService.isExternalSiren(service)
                    )
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
            )
        );
    }

    selectServicesList(isClareOneExcluded = false): Observable<{ data: DeviceListItem[]; counts: any }> {
        return combineLatest([
            this.selectServices(),
            this.selectDevices(),
            this.zoneDispatcher.getZoneState(),
            this.selectLiteServices(),
            this.selectDeviceMetadata(),
        ]).pipe(
            filter(([services]) => !!services && !!Object.values(services).length),
            map(([services, devices, zones, liteServices, deviceMetadata]) => {
                DeviceMetadataParser.setPathToDevicePropertyInfo(deviceMetadata, devices);
                const servicesList = { data: [], counts: {} };
                Object.values(services)
                    .filter(service => !isClareOneExcluded || service.instanceId !== CLAREONE_SERVICE_ID)
                    .forEach(service => {
                        if (service && !AppHelperService.isExternalSiren(service)) {
                            const serviceZoneNames = [];

                            if (zones.result && service.zoneUids) {
                                service.zoneUids.forEach(serviceZone =>
                                    zones.result[serviceZone] ? serviceZoneNames.push(zones.result[serviceZone].name) : null
                                );
                            }

                            const serviceCategories = (service.categories || []).map(category => this.getDeviceCategoryName(category));

                            const type = devices ? this.getDeviceType(devices, service) : "";
                            const onlineStatus = this.getStatus(service, liteServices ? liteServices[service.instanceId] : null);
                            servicesList.data.push({
                                id: service.instanceId,
                                name: service.name,
                                area: serviceZoneNames.join(", "),
                                categories: serviceCategories.join(", "),
                                type,
                                onlineStatus,
                                controlPoints: service.controlPoints,
                                deviceLowBattery: null,
                            });
                            if (!servicesList.counts[onlineStatus[0].toLowerCase()]) {
                                servicesList.counts[onlineStatus[0].toLowerCase()] = 1;
                            } else {
                                servicesList.counts[onlineStatus[0].toLowerCase()]++;
                            }
                        }
                    });

                servicesList.data.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
                return servicesList;
            })
        );
    }

    getDeviceType(devices, service) {
        let type = "";

        const device: CHDevice = devices[service?.derivedServiceDevice];
        if (device) {
            if (device.info && device.info.type) {
                type = `${device.info.manufacturer || ""} ${device.info.type || ""}`;
            } else if (device.Vendor) {
                type = `${device.Vendor} ${LocalizerService.getDeviceCategory(service.categories[0])}`;
            } else {
                type = device.ProtocolAdapterName;
            }
        } else if (service?.devicesUsedInService.length) {
            const types = [];
            for (const deviceUid of service.devicesUsedInService) {
                const usedDevice = devices[deviceUid];
                if (usedDevice) {
                    types.push(usedDevice.ProtocolAdapterName);
                }
            }

            type = types.join(", ");
        }

        return type;
    }

    selectProjectServicesList(): Observable<CHService[]> {
        return this.selectProjectServiceAsList();
    }

    selectDevices() {
        return this.selectDevicesState$().pipe(
            filter(result => result.callState === LoadingState.loaded),
            map(result => result.result)
        );
    }

    selectDevicesState$() {
        return this.store.pipe(
            select(CHDeviceSelectors.selectChDevicesState),
            filter(result => !!result?.uuid),
            distinctUntilChanged(),
            switchMap(result => this.isProjectOnline$(result)),
            tap(result => this.initDeviceMap(result))
        );
    }

    selectServicesState() {
        return this.store.pipe(
            select(CHServiceSelectors.selectChServicesState),
            filter(result => !!result?.uuid),
            distinctUntilChanged(),
            switchMap(result => this.isProjectOnline$(result)),
            tap(result => this.initDeviceMap(result))
        );
    }

    fetchService$(instanceId: string) {
        return this.store.pipe(
            select(CHServiceSelectors.selectChService(instanceId)),
            distinctUntilChanged(),
            switchMap(result => this.isProjectOnline$(result)),
            tap(result => this.initDeviceMap(result))
        );
    }

    selectDevicesByUuid() {
        return this.store.pipe(select(CHDeviceSelectors.selectChDevicesState));
    }

    doesIncludeDeviceClass(uid: string, deviceClass: string) {
        return this.selectDevices().pipe(
            map(devices => {
                if (devices) {
                    return devices[uid]?.DeviceClasses.includes(deviceClass);
                }
                return false;
            })
        );
    }

    selectDeviceClassObjectState(deviceUid: string, dcoName: string) {
        return this.store.pipe(
            select(CHDeviceSelectors.selectDeviceClassObjectState(deviceUid, dcoName)),
            map(result => result.callState)
        );
    }

    selectDeviceClassObjectProperty$(deviceUid: string, dco: string, property: string) {
        return this.getUuid().pipe(
            filter(result => !!result),
            switchMap(uuid =>
                this.selectDeviceClassObject$(uuid, deviceUid, dco).pipe(
                    filter(selectedDco => !!selectedDco),
                    map(deviceClass => {
                        if (deviceClass?.Properties?.[property]) {
                            return deviceClass.Properties[property];
                        }
                        return deviceClass;
                    }),
                    distinctUntilChanged()
                )
            )
        );
    }

    selectDeviceClassObjectPropertyValue(deviceUid: string, dco: string, property: string) {
        return this.selectDeviceClassObjectProperty$(deviceUid, dco, property).pipe(
            map(deviceClassProperty => {
                if (deviceClassProperty?.hasOwnProperty("Value")) {
                    return deviceClassProperty.Value;
                }
                return deviceClassProperty;
            })
        );
    }

    getDevicesByProtocolAdapter(protocolAdapter: string) {
        return this.store.pipe(select(CHDeviceSelectors.getChDevicesByProtocolAdapter(protocolAdapter)));
    }

    public getDeviceByUid(uid: string) {
        return this.store.pipe(select(CHDeviceSelectors.getChDeviceByUid(uid)));
    }

    public selectService(instanceId: string): Observable<CHService> {
        return this.store.pipe(select(CHServiceSelectors.selectChService(instanceId)));
    }

    selectDeviceClassObjectNoLoad$(deviceUid: string, dco: string) {
        return this.store.pipe(
            select(CHDeviceSelectors.selectDeviceClassObjectState(deviceUid, dco)),
            distinctUntilChanged((prev, current) => prev?.callState === current?.callState && prev?.result === current?.result),
            map(result => result?.result)
        );
    }

    selectDeviceClassObject$(uuid: string, deviceUid: string, dco: string) {
        return this.store.pipe(
            select(CHDeviceSelectors.selectDeviceClassObjectState(deviceUid, dco)),
            distinctUntilChanged((prev, current) => prev?.callState === current?.callState && prev?.result === current?.result),
            switchMap(result => this.isProjectOnline$(result)),
            map(result => {
                if (result?.callState === LoadingState.loaded) {
                    return result.result || result.error;
                }

                if (!result || result?.callState === LoadingState.init) {
                    this.store.dispatch(
                        new DevicesActions.GetDeviceClassObjects({
                            uuid,
                            deviceUid,
                            dco,
                        })
                    );
                }

                return null;
            })
        );
    }

    isLoadingDeviceInfo() {
        return this.store.pipe(select(CHServiceSelectors.isLoadingCurrentProjectServiceAndDeviceInfo));
    }

    getServiceToDeviceMapState() {
        return this.store.pipe(
            select(CHServiceSelectors.selectChServicesState),
            distinctUntilChanged(),
            filter(result => !!result)
        );
    }

    getDeviceState() {
        return this.store.pipe(
            select(CHDeviceSelectors.selectChDevicesState),
            distinctUntilKeyChanged("callState"),
            filter(result => !!result)
        );
    }

    updateServiceNameAndZone(
        uuid: string,
        instanceId: string,
        name: string,
        zoneUids: string[] = [],
        callbackEmitter?: EventEmitter<any>,
        logAction?: any
    ) {
        this.store.dispatch(new UpdateServiceNameAndZone({ uuid, instanceId, name, zoneUids, callbackEmitter, logAction }));
    }

    selectServices(): Observable<ClareHomeDbCollection<CHService>> {
        return this.store.pipe(
            select(CHServiceSelectors.selectChServicesState),
            distinctUntilChanged(),
            filter(result => result?.callState === LoadingState.loaded),
            map(result => result.result)
        );
    }

    selectProjectServiceAsList(): Observable<CHService[]> {
        return this.store.pipe(select(CHServiceSelectors.getChServiceListForCurrentProject), distinctUntilChanged());
    }

    getServiceToDeviceMap(): Observable<ClareHomeDbCollection<CHService>> {
        return this.store.pipe(
            select(CHServiceSelectors.selectChServicesState),
            distinctUntilChanged(),
            filter(result => !!result),
            switchMap(result => this.isProjectOnline$(result)),
            tap(result => this.initDeviceMap(result)),
            map(result => result.result)
        );
    }

    public initDeviceMap(result: any) {
        if (result.callState === LoadingState.init) {
            this.store.dispatch(new GetServiceToDeviceMap({ uuid: result.uuid }));
            this.store.dispatch(new GetDevicesSilent({ uuid: result.uuid }));
        }
    }

    deleteService(uuid: string, service: CHService, callbackEmitter?: EventEmitter<any>) {
        this.store.dispatch(new DeleteServiceConfirmation({ uuid, data: service, callbackEmitter }));
    }

    deleteDevice(
        uuid: string,
        device: CHDevice,
        serviceOfDevice?: CHService,
        callbackEmitter?: EventEmitter<any>,
        deviceName?: string,
        logAction?: AuditLogInput
    ) {
        this.store.dispatch(new DeleteServiceMap({ uuid, device, service: serviceOfDevice, deviceName, callbackEmitter, logAction }));
    }

    deleteDeviceNoConfirmation(
        uuid: string,
        device: CHDevice,
        serviceOfDevice?: CHService,
        callbackEmitter?: EventEmitter<any>,
        logAction?: AuditLogInput
    ) {
        this.store.dispatch(new DeleteDevice({ uuid, data: device, service: serviceOfDevice, callbackEmitter, logAction }));
    }

    deleteAllDevicesByDeviceType(
        uuid: string,
        driverName: string,
        adapterName: string,
        addMethod: string,
        callbackEmitter?: EventEmitter<any>,
        logAction?: AuditLogInput
    ) {
        this.store.dispatch(new DeleteAllDevices({ uuid, driverName, adapterName, addMethod, callbackEmitter, logAction }));
    }

    deleteSensor(uuid: string, device: SecurityZoneInfo | SecurityFobInfo, callbackEmitter: EventEmitter<any>) {
        this.store.dispatch(new DeleteSensorConfirmation({ uuid, securityZone: device, callbackEmitter }));
    }

    resetDevicesCallState() {
        this.store.dispatch(new ResetDevicesCallState());
    }

    clearDevicesState() {
        this.store.dispatch(new ClearDevicesState());
    }

    clearDCOState(deviceUid: string, deviceClass?: string) {
        this.store.dispatch(new ClearDCOState({ deviceUid, deviceClass }));
    }

    clearAllDCOs() {
        this.store.dispatch(new ClearAllDCOs());
    }

    resetDCOState(deviceUid: string, deviceClass?: string) {
        this.store.dispatch(new ResetDCOState({ deviceUid, deviceClass }));
    }

    public selectChildDevices(parentDevice: CHDevice) {
        return this.store.pipe(select(DeviceSelectors.getChildDevices(parentDevice)));
    }

    public selectServiceMetadataForCurrentProject() {
        return this.store.pipe(select(ServiceMetadataSelectors.selectChServiceMetadata));
    }

    selectDeviceMetadata(): Observable<ClareHomeDbCollection<IDeviceMetadataNode | any> | CHDeviceMetadata> {
        return this.store.pipe(
            select(DeviceMetadataSelectors.selectChDeviceMetadataState),
            filter(result => !!result?.uuid),
            tap(result => {
                if (!result || result.callState === LoadingState.init) {
                    this.store.dispatch(new GetDeviceMetadata({ uuid: result.uuid }));
                }
            }),
            map(result => result.result)
        );
    }

    selectDeviceMetadataInstallHelpByCategory(category) {
        return this.store.pipe(
            select(DeviceMetadataSelectors.getChDeviceCategoryData),
            map(res => res[category]?.deviceTypes)
        );
    }

    selectDeviceMetadataInstallHelp(category: string, protocolAdapterName: string): Observable<DeviceInstallMetadata[]> {
        return this.store.pipe(select(DeviceMetadataSelectors.getInstallHelpTextByCategory(category, protocolAdapterName)));
    }

    getChCategories() {
        return this.store.pipe(select(DeviceMetadataSelectors.getChDeviceCategoryMetaDataSorted));
    }

    clearServiceState() {
        this.store.dispatch(new ClearServiceState());
        this.store.dispatch(new ClearLiteServiceState());
    }

    redirectToEditDevice(uids: string[], fetchServices = true, shouldNavigate = true, addedZwave = false) {
        let projectId: number;
        let locationUuid: string;
        return this.findIdParam().pipe(
            switchMap(id => {
                projectId = +id || null;
                return this.getUuid();
            }),
            switchMap(uuid => {
                locationUuid = uuid;
                if (fetchServices) {
                    this.clearServiceState();
                }
                return this.getServiceToDeviceMap();
            }),
            map(services => {
                if (services) {
                    const service = Object.values(services).find(
                        value => value.derivedServiceDevice && uids.includes(value.derivedServiceDevice)
                    );

                    const serviceInstanceId = encodeURIComponent(service?.instanceId);
                    let rootPath = `${accountRoutePaths.root}/${accountRoutePaths.edit}`;
                    let id: number | string = projectId;

                    if (shouldNavigate) {
                        if (isEmbedded()) {
                            this.routerService.updateBackButton({
                                action: null,
                            });
                            rootPath = "/myaccount/locations/";
                            id = locationUuid;
                        }

                        this.navigate([rootPath, id, "devices", "edit", serviceInstanceId], { addedZwave });

                        return true;
                    }

                    return serviceInstanceId;
                }
            })
        );
    }

    private getServiceMetaData(result: CHServiceMetadataState) {
        if (!result || result.callState === LoadingState.init) {
            this.store.dispatch(new GetServiceMetadata({ uuid: result.uuid }));
        }
    }

    selectServiceCategoryListByUuid() {
        return this.store.pipe(select(ServiceMetadataSelectors.getChServicesFromMetadata));
    }

    selectServicesByCategory(category: string) {
        return this.store.pipe(select(ServiceMetadataSelectors.selectServicesByCategory(category)));
    }

    getChServiceCategoriesList() {
        return this.store.pipe(select(ServiceMetadataSelectors.getChServiceCategoriesList));
    }

    getChServiceCategoriesListByUuid(categoryToSelect?: string) {
        return this.store.pipe(
            select(ServiceMetadataSelectors.getChServiceCategoriesListByUuid),
            map(result => {
                if (categoryToSelect) {
                    return result.find(category => category.name === categoryToSelect)?.services || [];
                }

                return result;
            })
        );
    }

    getChServiceControlPointMetadata(service) {
        return this.store.pipe(select(ServiceMetadataSelectors.getChServiceControlPointMetadata(service)));
    }

    selectServiceMetadata() {
        return this.store.pipe(
            select(ServiceMetadataSelectors.selectChServiceMetadataState),
            filter(result => !!result?.uuid),
            tap(result => this.getServiceMetaData(result)),
            map(result => result.result)
        );
    }

    selectServiceMetadataLoadingState() {
        return this.store.pipe(select(ServiceMetadataSelectors.selectServiceMetadataCallState));
    }

    selectDeviceMetadataLoadingState() {
        return this.store.pipe(select(DeviceMetadataSelectors.selectDeviceMetadataCallState));
    }

    selectServiceLoadingState() {
        return this.store.pipe(select(CHServiceSelectors.loadingServiceState));
    }

    dispatchDeviceProject(uuid) {
        const decodedUuid = decodeURIComponent(uuid);
        this.store.dispatch(new ProjectActions.GetActiveProjectByUuid(decodedUuid));
    }

    clearDeviceMetadataState() {
        this.store.dispatch(new ClearDeviceMetadata());
    }

    clearServiceMetadataState() {
        this.store.dispatch(new ClearServiceMetadata());
    }

    selectZWaveDevicesState() {
        return this.store.pipe(
            select(CHDeviceSelectors.selectChZWaveDevicesState),
            filter(result => !!result?.uuid),
            distinctUntilChanged(),
            tap(result => {
                if (result.callState === LoadingState.init) {
                    this.store.dispatch(new GetZWaveDevices({ uuid: result.uuid }));
                }
            })
        );
    }

    clearZWaveDevicesState() {
        this.store.dispatch(new ClearZWaveDevicesState());
    }

    fetchDeviceLowBatteryInfo$(devices: DeviceListItem[]): Observable<DeviceListItem[]> {
        return this.selectDevices().pipe(
            distinctUntilChanged(),
            switchMap(deviceList => {
                const observables = devices.map(device => {
                    const batteryLevelControlPoint = device?.controlPoints?.batteryLevel;
                    if (batteryLevelControlPoint && !!deviceList[batteryLevelControlPoint.deviceUid]) {
                        return this.selectDeviceClassObjectProperty$(
                            batteryLevelControlPoint.deviceUid,
                            DEVICE_CLASSES.BATTERY_LEVEL,
                            "batteryLevel"
                        ).pipe(
                            distinctUntilChanged(),
                            filter(property => !!property),
                            map((property: DeviceClassObjectProperty) => {
                                const batteryLevel = property?.Value as number;
                                device.deviceLowBattery = !!batteryLevel && batteryLevel < 20;
                                return device;
                            }),
                            catchError(err => {
                                console.error(err);
                                return of(device);
                            })
                        );
                    }

                    return of(device);
                });

                return combineLatest(observables);
            })
        );
    }

    constructor(
        private readonly store: Store<CHDeviceState | ProjectState>,
        private readonly zoneDispatcher: ZoneDispatcher,
        private readonly projectStatusService: ProjectStatusService,
        private readonly routerService: RouterService
    ) {}
}
